<template>
  <nv-page header="Domain Discovery" data-cy="domain-discovery">
    <template #toolbar>
      <v-spacer />
      <v-btn
        v-if="mavV1Enabled && items.length"
        :class="['toggle-mav-btn mr-2', { 'toggle-mav-btn--remove': allItemsInMav }]"
        outlined
        small
        @click="toggleAllMav"
      >
        <v-icon small class="mr-1">{{
          allItemsInMav ? 'mdi-minus-box-multiple' : 'mdi-plus-box-multiple'
        }}</v-icon>
        {{ allItemsInMav ? 'Remove All from MAV' : 'Add All to MAV' }}
      </v-btn>
      <v-btn
        v-if="mavV1Enabled && mavItems.length > 0"
        class="view-mav-btn mr-2"
        dark
        @click="mavDialog = true"
      >
        <v-icon small class="mr-1">mdi-robot</v-icon>
        View MAV Items ({{ mavItems.length }})
      </v-btn>
      <nv-btn
        v-if="items.length && $auth.hasPermissions('ant.discovery')"
        type="lock"
        data-cy="lock-btn"
        @click="reserve()"
      />
      <nv-table-search v-model="search" sync class="ml-4" data-cy="search" />
    </template>

    <template #filters>
      <nv-table-filters
        ref="applied-filters"
        v-model="domainFilters"
        :no-clear="isDefaultFilters"
        @clear="$refs['filter-sheet'].clearFilters()"
      >
        <filter-sheet ref="filter-sheet" @changed="setTableFilters" @update:source="sourceUpdate" />
      </nv-table-filters>
    </template>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :options.sync="table"
      :server-items-length="totalItems"
      :footer-props="footerProps"
      :items-per-page.sync="table.itemsPerPage"
      must-sort
    >
      <template #item="{ item }">
        <tr>
          <td class="domain-cell">
            <div class="domain-cell-content">
              <a :href="'http://' + item.domain" target="_blank">{{ item.domain }}</a>
              <v-btn
                v-if="mavV1Enabled"
                x-small
                class="add-mav-btn"
                :class="{ 'add-mav-btn--added': isInMavList(item) }"
                :disabled="isInMavList(item)"
                @click.stop="addToMav(item)"
              >
                <v-icon x-small>{{ isInMavList(item) ? 'mdi-check' : 'mdi-plus' }}</v-icon>
                <span class="add-mav-btn-text">{{
                  isInMavList(item) ? 'Added' : 'Add to MAV'
                }}</span>
              </v-btn>
            </div>
          </td>
          <td>{{ item.countries.join(', ') | uppercase }}</td>
          <td>
            <template v-if="table.sortBy[0] === 'rank_desktop'">
              {{ item.hits_desktop | number }}
            </template>
            <template v-else-if="table.sortBy[0] === 'rank_mobile'">
              {{ item.hits_mobile | number }}
            </template>
            <template v-else>{{ item.hits | number }}</template>
          </td>
          <td>{{ item.last_seen_on | date }}</td>
        </tr>
      </template>
    </v-data-table>

    <!-- MAV Items Dialog -->
    <v-dialog
      v-if="mavV1Enabled"
      v-model="mavDialog"
      max-width="700"
      persistent
      scrollable
      overlay-color="#000"
      overlay-opacity="0.72"
      content-class="mav-items-dialog-shell"
    >
      <div class="mav-items-dialog">
        <!-- Header -->
        <div class="mav-items-header">
          <div class="mav-items-header-icon">
            <v-icon small color="white">mdi-robot</v-icon>
          </div>
          <div class="mav-items-header-text">
            <div class="mav-items-header-title">MAV Items</div>
            <div class="mav-items-header-sub">
              {{ mavItems.length }} {{ mavItems.length === 1 ? 'domain' : 'domains' }} selected for
              prediction
            </div>
          </div>
          <v-btn icon small @click="mavDialog = false">
            <v-icon small color="#b0bec5">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Body -->
        <div class="mav-items-body">
          <!-- Empty state -->
          <div v-if="mavItems.length === 0" class="mav-items-empty">
            <v-icon large class="mav-items-empty-icon">mdi-inbox-outline</v-icon>
            <div>No domains added yet</div>
          </div>

          <!-- List -->
          <div v-else class="mav-items-list">
            <div class="mav-items-list-header">
              <div class="col-num">#</div>
              <div class="col-domain">Domain</div>
              <div class="col-countries">Countries</div>
              <div class="col-action"></div>
            </div>

            <div v-for="(mavItem, index) in mavItems" :key="mavItem.domain" class="mav-items-row">
              <div class="col-num">{{ index + 1 }}</div>
              <div class="col-domain">
                <a :href="'http://' + mavItem.domain" target="_blank" class="mav-domain-link">
                  {{ mavItem.domain }}
                </a>
              </div>
              <div class="col-countries">
                <span
                  v-if="mavItem.countries && mavItem.countries.length"
                  class="mav-countries-text"
                >
                  {{ mavItem.countries.join(', ').toUpperCase() }}
                </span>
                <span v-else class="mav-locale-empty">—</span>
              </div>
              <div class="col-action">
                <v-btn icon x-small @click="removeFromMav(mavItem)">
                  <v-icon small color="#ef5350">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="mav-items-footer">
          <v-btn text @click="mavDialog = false">Cancel</v-btn>
          <v-btn v-if="mavItems.length > 0" text color="error" @click="clearAllMavItems">
            <v-icon small class="mr-1">mdi-delete-sweep</v-icon>
            Remove All
          </v-btn>
          <v-spacer />
          <v-btn
            :loading="mavLoading"
            :disabled="mavItems.length === 0"
            class="mav-btn-primary"
            dark
            @click="useMav"
          >
            <v-icon small class="mr-1">mdi-robot</v-icon>
            Use MAV
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </nv-page>
</template>

<script>
import FilterSheet from '@/components/cls/domain/FilterSheet';
import DiscoveryList from '@/components/cls/DiscoveryList';
import HTTP from '@/api/ant';
import { getConfigMap } from '@/helpers/filters';

const headers = [
  { text: 'Domain', sortable: false, value: 'domain' },
  { text: 'Countries', sortable: false, value: 'countries' },
  { text: 'Audience', sortable: false, value: 'rank' },
  { text: 'Last Seen', sortable: false, value: 'last_seen_on' },
];

const MAV_STORAGE_KEY = 'owl_mav_discovery_items';
const MAX_MAV_ITEMS = 50;

export default {
  components: { FilterSheet },
  extends: DiscoveryList('domain'),
  data: () => ({
    headers,
    mavItems: [],
    mavDialog: false,
    mavLoading: false,
  }),
  computed: {
    domainFilters() {
      const s = {};
      if (this.table.sortBy[0] !== 'rank') {
        s.domainSource = this.table.sortBy[0];
      }
      return { ...this.formattedFilters, ...s };
    },
    isDefaultFilters() {
      return Object.values(this.domainFilters).filter(v => !!v).length === 1;
    },
    allItemsInMav() {
      if (!this.items.length) return true;
      return this.items.every(item => this.isInMavList(item));
    },
    mavV1Enabled() {
      const config = getConfigMap.call(this);
      return config['mavFeature_owl_ui']?.enabled?.v1 === true;
    },
  },
  created() {
    this.loadMavItems();
  },
  methods: {
    sourceUpdate(source) {
      this.table.sortBy = source ? [source] : ['rank'];
    },
    loadMavItems() {
      try {
        const stored = localStorage.getItem(MAV_STORAGE_KEY);
        this.mavItems = stored ? JSON.parse(stored) : [];
      } catch (e) {
        this.mavItems = [];
      }
    },
    saveMavItems() {
      localStorage.setItem(MAV_STORAGE_KEY, JSON.stringify(this.mavItems));
    },
    isInMavList(item) {
      return this.mavItems.some(m => m.domain === item.domain);
    },
    addToMav(item) {
      if (this.isInMavList(item)) return;
      if (this.mavItems.length >= MAX_MAV_ITEMS) {
        this.$flash(
          'warning',
          `MAV list is full (max ${MAX_MAV_ITEMS} items). Remove some items first.`,
        );
        return;
      }
      this.mavItems.push({
        id: item.id,
        domain: item.domain,
        countries: item.countries || [],
      });
      this.saveMavItems();
      this.$flash('success', `Added ${item.domain} to MAV list`);
    },
    addAllToMav() {
      const availableSlots = MAX_MAV_ITEMS - this.mavItems.length;
      if (availableSlots <= 0) {
        this.$flash(
          'warning',
          `MAV list is full (max ${MAX_MAV_ITEMS} items). Remove some items first.`,
        );
        return;
      }

      const itemsToAdd = this.items.filter(item => !this.isInMavList(item));
      const itemsToAddLimited = itemsToAdd.slice(0, availableSlots);
      const skippedCount = itemsToAdd.length - itemsToAddLimited.length;

      itemsToAddLimited.forEach(item => {
        this.mavItems.push({
          id: item.id,
          domain: item.domain,
          countries: item.countries || [],
        });
      });

      if (itemsToAddLimited.length > 0) {
        this.saveMavItems();
        if (skippedCount > 0) {
          this.$flash(
            'warning',
            `Added ${itemsToAddLimited.length} domains. ${skippedCount} domains skipped (max ${MAX_MAV_ITEMS} items).`,
          );
        } else {
          this.$flash('success', `Added ${itemsToAddLimited.length} domains to MAV list`);
        }
      }
    },
    removeAllFromMav() {
      const currentDomains = this.items.map(item => item.domain);
      const initialCount = this.mavItems.length;
      this.mavItems = this.mavItems.filter(m => !currentDomains.includes(m.domain));
      const removedCount = initialCount - this.mavItems.length;
      if (removedCount > 0) {
        this.saveMavItems();
        this.$flash('success', `Removed ${removedCount} domains from MAV list`);
      }
    },
    toggleAllMav() {
      if (this.allItemsInMav) {
        this.removeAllFromMav();
      } else {
        this.addAllToMav();
      }
    },
    removeFromMav(item) {
      const index = this.mavItems.findIndex(m => m.domain === item.domain);
      if (index > -1) {
        this.mavItems.splice(index, 1);
        this.saveMavItems();
      }
    },
    clearMavItems() {
      this.mavItems = [];
      localStorage.removeItem(MAV_STORAGE_KEY);
    },
    clearAllMavItems() {
      const count = this.mavItems.length;
      this.clearMavItems();
      this.$flash('success', `Removed all ${count} domains from MAV list`);
    },
    async useMav() {
      if (this.mavItems.length === 0) return;

      this.mavLoading = true;

      try {
        // Build the domains to lock
        const domains = this.mavItems.map(m => m.domain);

        // Lock the selected domains using the bulk lock endpoint
        const response = await HTTP.put('/domain/discoveries/lock', {
          domains: domains,
        });

        if (response.data && response.data.count > 0) {
          this.$flash('success', `Locked ${response.data.count} domains for MAV prediction`);

          // Clear MAV items from storage
          this.clearMavItems();

          // Close the dialog
          this.mavDialog = false;

          // Navigate to classify page with openMav query param
          this.$router.push({
            name: 'owl.domain.classify.list',
            query: { openMav: 'true' },
          });
        } else {
          this.$flash(
            'warning',
            'No domains were locked. They may already be locked or not available.',
          );
        }
      } catch (error) {
        this.$flash('error', error.response?.data?.message || 'Failed to lock domains for MAV');
      } finally {
        this.mavLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Domain cell with aligned buttons */
.domain-cell {
  min-width: 300px;
}

.domain-cell-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.domain-cell-content a {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.add-mav-btn {
  flex-shrink: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  min-width: 28px !important;
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  font-size: 11px !important;
  text-transform: none !important;
  border-radius: 4px !important;
  overflow: hidden !important;
  transition: width 0.25s ease, min-width 0.25s ease, padding 0.25s ease !important;
}

.add-mav-btn:hover {
  width: 110px !important;
  min-width: 110px !important;
  padding: 0 12px !important;
}

.add-mav-btn-text {
  display: inline-block;
  max-width: 0;
  overflow: hidden;
  white-space: nowrap;
  opacity: 0;
  margin-left: 0;
  transition: max-width 0.25s ease, opacity 0.2s ease, margin-left 0.25s ease;
}

.add-mav-btn:hover .add-mav-btn-text {
  max-width: 80px;
  opacity: 1;
  margin-left: 4px;
}

.add-mav-btn--added {
  background: #4caf50 !important;
  cursor: default;
  width: 28px !important;
  min-width: 28px !important;
}

.add-mav-btn--added:hover {
  width: 80px !important;
  min-width: 80px !important;
}

/* View MAV button in toolbar */
.view-mav-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3) !important;
}

/* Add All to MAV button */
.toggle-mav-btn {
  border-color: #667eea !important;
  color: #667eea !important;
  text-transform: none !important;
}
.toggle-mav-btn:hover {
  background: rgba(102, 126, 234, 0.08) !important;
}
.toggle-mav-btn--remove {
  border-color: #ef5350 !important;
  color: #ef5350 !important;
}
.toggle-mav-btn--remove:hover {
  background: rgba(239, 83, 80, 0.08) !important;
}

/* ─── MAV Items Dialog (matching MavBatchDialog styles) ─── */
::v-deep .mav-items-dialog-shell {
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.5) !important;
  background-color: transparent !important;
  overflow: visible !important;
}

.mav-items-dialog {
  background: #394556;
  color: #eceff1;
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  overflow: hidden;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

/* Header */
.mav-items-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: #394556;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}
.mav-items-header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  flex-shrink: 0;
}
.mav-items-header-text {
  flex: 1;
  min-width: 0;
}
.mav-items-header-title {
  color: #eceff1;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
}
.mav-items-header-sub {
  color: #78909c;
  font-size: 12px;
  margin-top: 2px;
}

/* Body */
.mav-items-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  background: #394556;
  min-height: 0;
  max-height: 400px;
}

/* Empty state */
.mav-items-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #546e7a;
  font-size: 13px;
  gap: 4px;
}
.mav-items-empty-icon {
  color: rgba(255, 255, 255, 0.12) !important;
  margin-bottom: 8px;
}

/* List */
.mav-items-list {
  display: flex;
  flex-direction: column;
}
.mav-items-list-header,
.mav-items-row {
  display: grid;
  grid-template-columns: 44px 1fr 180px 40px;
  align-items: center;
  padding: 0 20px;
  gap: 12px;
}
.mav-items-list-header {
  height: 36px;
  color: #78909c;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
}
.mav-items-row {
  height: 44px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background 0.12s;
}
.mav-items-row:hover {
  background: rgba(255, 255, 255, 0.02);
}

/* Columns */
.col-num {
  color: #546e7a;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.col-domain {
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.col-countries {
  min-width: 0;
  text-align: right;
  overflow: hidden;
  white-space: nowrap;
}
.col-action {
  text-align: right;
}

/* Domain link */
.mav-domain-link {
  color: #eceff1;
  text-decoration: none;
  font-size: 13px;
}
.mav-domain-link:hover {
  color: #ce93d8;
  text-decoration: underline;
}

/* Locale chip */
/* Remove the entire .mav-locale-chip rule block (lines 609-626) */
.mav-countries-text {
  color: #b0bec5;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mav-locale-empty {
  color: #546e7a;
  font-size: 12px;
}

/* Footer */
.mav-items-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #2f3945;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

/* Primary button */
.mav-btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3) !important;
  letter-spacing: 0.02em !important;
}
</style>
